import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, ArrowLeft, ArrowRight, FileJson, FileSpreadsheet, Database, 
  Check, X, AlertTriangle, Loader2, FileText, Eye, Settings2, Play,
  CheckCircle2, XCircle, ChevronDown, ChevronRight, RefreshCw,
  Download, Trash2, HelpCircle, Zap, FileCheck, Package
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { importacionAPI } from '../api';
import { toast } from '../hooks/use-toast';

const PASOS = [
  { id: 1, nombre: 'Subir archivos', descripcion: 'Sube los archivos de exportación' },
  { id: 2, nombre: 'Análisis', descripcion: 'Revisión de datos detectados' },
  { id: 3, nombre: 'Mapeo', descripcion: 'Configurar correspondencias' },
  { id: 4, nombre: 'Importar', descripcion: 'Ejecutar importación' },
];

const FORMATOS_SOPORTADOS = [
  { ext: '.json', nombre: 'JSON', icon: FileJson },
  { ext: '.csv', nombre: 'CSV', icon: FileSpreadsheet },
  { ext: '.xlsx', nombre: 'Excel', icon: FileSpreadsheet },
  { ext: '.sql', nombre: 'SQL Dump', icon: Database },
  { ext: '.zip', nombre: 'ZIP (múltiples archivos)', icon: Package },
];

const ImportacionDatos = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Estado del wizard
  const [pasoActual, setPasoActual] = useState(1);
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  
  // Datos de análisis
  const [analisis, setAnalisis] = useState(null);
  const [entidadesDestino, setEntidadesDestino] = useState({});
  
  // Mapeos editables
  const [mapeos, setMapeos] = useState([]);
  const [entidadesSeleccionadas, setEntidadesSeleccionadas] = useState({});
  
  // Vista previa
  const [previewData, setPreviewData] = useState(null);
  const [previewEntidad, setPreviewEntidad] = useState(null);
  
  // Resultados
  const [resultados, setResultados] = useState(null);
  const [importando, setImportando] = useState(false);
  const [progresoImportacion, setProgresoImportacion] = useState(0);

  // ============== PASO 1: SUBIR ARCHIVOS ==============
  
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setArchivosSeleccionados(prev => [...prev, ...files]);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    setArchivosSeleccionados(prev => [...prev, ...files]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const removeFile = (index) => {
    setArchivosSeleccionados(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    if (ext === 'json') return <FileJson className="w-5 h-5 text-yellow-600" />;
    if (['csv', 'xlsx', 'xls'].includes(ext)) return <FileSpreadsheet className="w-5 h-5 text-green-600" />;
    if (ext === 'sql') return <Database className="w-5 h-5 text-blue-600" />;
    if (ext === 'zip') return <Package className="w-5 h-5 text-purple-600" />;
    return <FileText className="w-5 h-5 text-gray-600" />;
  };

  const subirArchivos = async () => {
    if (archivosSeleccionados.length === 0) {
      toast({ title: "Error", description: "Selecciona al menos un archivo", variant: "destructive" });
      return;
    }
    
    try {
      setCargando(true);
      const response = await importacionAPI.subir(archivosSeleccionados);
      setSessionId(response.data.session_id);
      setAnalisis(response.data.analisis);
      
      // Cargar entidades destino
      const destResponse = await importacionAPI.getEntidadesDestino();
      setEntidadesDestino(destResponse.data);
      
      // Inicializar mapeos con las sugerencias
      const mapeosIniciales = response.data.analisis.mapeos_sugeridos.map(m => ({
        ...m,
        activo: m.confianza >= 0.5
      }));
      setMapeos(mapeosIniciales);
      
      // Inicializar selección de entidades
      const seleccion = {};
      response.data.analisis.entidades.forEach(e => {
        const mapeo = mapeosIniciales.find(m => m.entidad_origen === e.nombre);
        seleccion[e.nombre] = mapeo?.activo || false;
      });
      setEntidadesSeleccionadas(seleccion);
      
      setPasoActual(2);
      toast({ title: "Archivos analizados correctamente" });
    } catch (error) {
      toast({ title: "Error al analizar archivos", description: error.message, variant: "destructive" });
    } finally {
      setCargando(false);
    }
  };

  // ============== PASO 2: ANÁLISIS ==============
  
  const verPreview = async (entidad) => {
    try {
      const response = await importacionAPI.preview(sessionId, entidad, 5);
      setPreviewData(response.data.registros);
      setPreviewEntidad(entidad);
    } catch (error) {
      toast({ title: "Error", variant: "destructive" });
    }
  };

  const getConfianzaColor = (confianza) => {
    if (confianza >= 0.8) return "text-green-600 bg-green-50";
    if (confianza >= 0.5) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getConfianzaTexto = (confianza) => {
    if (confianza >= 0.8) return "Alta";
    if (confianza >= 0.5) return "Media";
    return "Baja";
  };

  // ============== PASO 3: MAPEO ==============
  
  const toggleEntidad = (nombreEntidad) => {
    setEntidadesSeleccionadas(prev => ({
      ...prev,
      [nombreEntidad]: !prev[nombreEntidad]
    }));
  };

  const actualizarMapeoEntidad = (entidadOrigen, entidadDestino) => {
    setMapeos(prev => {
      const existente = prev.find(m => m.entidad_origen === entidadOrigen);
      if (existente) {
        return prev.map(m => 
          m.entidad_origen === entidadOrigen 
            ? { ...m, entidad_destino: entidadDestino }
            : m
        );
      } else {
        // Crear nuevo mapeo
        const entidadAnalisis = analisis.entidades.find(e => e.nombre === entidadOrigen);
        return [...prev, {
          entidad_origen: entidadOrigen,
          entidad_destino: entidadDestino,
          confianza: 0.5,
          mapeos_campos: [],
          activo: true
        }];
      }
    });
  };

  const actualizarMapeoCampo = (entidadOrigen, campoOrigen, campoDestino) => {
    // Tratar "__none__" como vacío
    const valorDestino = campoDestino === "__none__" ? null : campoDestino;
    
    setMapeos(prev => prev.map(m => {
      if (m.entidad_origen !== entidadOrigen) return m;
      
      const camposActualizados = [...m.mapeos_campos];
      const idx = camposActualizados.findIndex(c => c.campo_origen === campoOrigen);
      
      if (idx >= 0) {
        if (valorDestino) {
          camposActualizados[idx] = { ...camposActualizados[idx], campo_destino: valorDestino };
        } else {
          camposActualizados.splice(idx, 1);
        }
      } else if (valorDestino) {
        camposActualizados.push({
          campo_origen: campoOrigen,
          campo_destino: valorDestino,
          confianza: 1.0,
          transformacion: null
        });
      }
      
      return { ...m, mapeos_campos: camposActualizados };
    }));
  };

  // ============== PASO 4: IMPORTAR ==============
  
  const ejecutarImportacion = async () => {
    const mapeosActivos = mapeos.filter(m => entidadesSeleccionadas[m.entidad_origen]);
    
    if (mapeosActivos.length === 0) {
      toast({ title: "Error", description: "Selecciona al menos una entidad para importar", variant: "destructive" });
      return;
    }
    
    try {
      setImportando(true);
      setProgresoImportacion(10);
      
      const response = await importacionAPI.ejecutar({
        session_id: sessionId,
        mapeos: mapeosActivos,
        opciones: {}
      });
      
      setProgresoImportacion(100);
      setResultados(response.data.resultados);
      toast({ title: "Importación completada" });
    } catch (error) {
      toast({ title: "Error en la importación", description: error.message, variant: "destructive" });
    } finally {
      setImportando(false);
    }
  };

  const reiniciar = () => {
    setArchivosSeleccionados([]);
    setSessionId(null);
    setAnalisis(null);
    setMapeos([]);
    setEntidadesSeleccionadas({});
    setResultados(null);
    setProgresoImportacion(0);
    setPasoActual(1);
  };

  // ============== RENDER ==============

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/configuracion')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Download className="w-6 h-6" />
              Importación de Datos
            </h1>
            <p className="text-gray-500">Importa datos desde otras aplicaciones</p>
          </div>
        </div>
        {pasoActual > 1 && !resultados && (
          <Button variant="outline" onClick={reiniciar}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Reiniciar
          </Button>
        )}
      </div>

      {/* Pasos */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {PASOS.map((paso, idx) => (
            <React.Fragment key={paso.id}>
              <div className={`flex items-center gap-3 ${pasoActual >= paso.id ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                  pasoActual > paso.id ? 'bg-green-100 text-green-600' :
                  pasoActual === paso.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100'
                }`}>
                  {pasoActual > paso.id ? <Check className="w-5 h-5" /> : paso.id}
                </div>
                <div className="hidden sm:block">
                  <p className="font-medium">{paso.nombre}</p>
                  <p className="text-xs text-gray-500">{paso.descripcion}</p>
                </div>
              </div>
              {idx < PASOS.length - 1 && (
                <div className={`flex-1 h-1 mx-4 rounded ${pasoActual > paso.id ? 'bg-green-300' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Contenido según paso */}
      {pasoActual === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Subir archivos de exportación</CardTitle>
            <CardDescription>
              Arrastra o selecciona los archivos exportados de tu aplicación anterior
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Formatos soportados */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-500">Formatos soportados:</span>
              {FORMATOS_SOPORTADOS.map(f => (
                <Badge key={f.ext} variant="outline" className="flex items-center gap-1">
                  <f.icon className="w-3 h-3" />
                  {f.nombre}
                </Badge>
              ))}
            </div>

            {/* Zona de drop */}
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed rounded-lg p-12 text-center cursor-pointer hover:bg-gray-50 transition-colors"
            >
              <Upload className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium">Arrastra archivos aquí</p>
              <p className="text-gray-500">o haz clic para seleccionar</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileSelect}
                accept=".json,.csv,.xlsx,.xls,.sql,.zip"
                className="hidden"
              />
            </div>

            {/* Archivos seleccionados */}
            {archivosSeleccionados.length > 0 && (
              <div className="space-y-2">
                <Label>Archivos seleccionados ({archivosSeleccionados.length})</Label>
                <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                  {archivosSeleccionados.map((archivo, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 hover:bg-gray-50">
                      <div className="flex items-center gap-3">
                        {getFileIcon(archivo.name)}
                        <div>
                          <p className="font-medium">{archivo.name}</p>
                          <p className="text-xs text-gray-500">{(archivo.size / 1024).toFixed(1)} KB</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeFile(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button 
              onClick={subirArchivos} 
              disabled={archivosSeleccionados.length === 0 || cargando}
              className="w-full"
            >
              {cargando ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analizando...</>
              ) : (
                <><Zap className="w-4 h-4 mr-2" />Analizar archivos</>
              )}
            </Button>
          </CardContent>
        </Card>
      )}

      {pasoActual === 2 && analisis && (
        <div className="space-y-6">
          {/* Resumen del análisis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="w-5 h-5" />
                Análisis completado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <p className="text-2xl font-bold text-blue-700">{analisis.entidades.length}</p>
                  <p className="text-sm text-blue-600">Entidades detectadas</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-700">{analisis.mapeos_sugeridos.length}</p>
                  <p className="text-sm text-green-600">Mapeos sugeridos</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <p className="text-2xl font-bold text-purple-700">{analisis.archivos_adjuntos}</p>
                  <p className="text-sm text-purple-600">Archivos adjuntos</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-700">{analisis.formato_detectado}</p>
                  <p className="text-sm text-gray-600">Formato detectado</p>
                </div>
              </div>

              {analisis.warnings.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="font-medium text-yellow-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Advertencias
                  </p>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                    {analisis.warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Entidades detectadas */}
          <Card>
            <CardHeader>
              <CardTitle>Entidades detectadas</CardTitle>
              <CardDescription>Revisa las entidades encontradas y su mapeo sugerido</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Entidad origen</TableHead>
                    <TableHead>Registros</TableHead>
                    <TableHead>Campos</TableHead>
                    <TableHead>Mapeo sugerido</TableHead>
                    <TableHead>Confianza</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {analisis.entidades.map((entidad) => {
                    const mapeo = mapeos.find(m => m.entidad_origen === entidad.nombre);
                    return (
                      <TableRow key={entidad.nombre}>
                        <TableCell className="font-medium">{entidad.nombre}</TableCell>
                        <TableCell>{entidad.registros.toLocaleString()}</TableCell>
                        <TableCell>{entidad.campos.length}</TableCell>
                        <TableCell>
                          {mapeo ? (
                            <Badge variant="outline" className="capitalize">
                              {mapeo.entidad_destino}
                            </Badge>
                          ) : (
                            <span className="text-gray-400">Sin mapeo</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {mapeo && (
                            <Badge className={getConfianzaColor(mapeo.confianza)}>
                              {getConfianzaTexto(mapeo.confianza)} ({(mapeo.confianza * 100).toFixed(0)}%)
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => verPreview(entidad.nombre)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => setPasoActual(3)}>
              Continuar al mapeo
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {pasoActual === 3 && analisis && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings2 className="w-5 h-5" />
                Configurar mapeo de campos
              </CardTitle>
              <CardDescription>
                Selecciona las entidades a importar y ajusta el mapeo de campos
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Accordion type="multiple" className="space-y-4">
                {analisis.entidades.map((entidad) => {
                  const mapeo = mapeos.find(m => m.entidad_origen === entidad.nombre);
                  const seleccionada = entidadesSeleccionadas[entidad.nombre];
                  
                  return (
                    <AccordionItem key={entidad.nombre} value={entidad.nombre} className="border rounded-lg">
                      <AccordionTrigger className="px-4 hover:no-underline">
                        <div className="flex items-center gap-4 flex-1">
                          <Checkbox
                            checked={seleccionada}
                            onCheckedChange={() => toggleEntidad(entidad.nombre)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className="flex-1 text-left">
                            <p className="font-medium">{entidad.nombre}</p>
                            <p className="text-sm text-gray-500">{entidad.registros} registros</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <Select
                              value={mapeo?.entidad_destino || ""}
                              onValueChange={(v) => actualizarMapeoEntidad(entidad.nombre, v)}
                            >
                              <SelectTrigger className="w-40" onClick={(e) => e.stopPropagation()}>
                                <SelectValue placeholder="Seleccionar..." />
                              </SelectTrigger>
                              <SelectContent>
                                {Object.keys(entidadesDestino).map(ed => (
                                  <SelectItem key={ed} value={ed} className="capitalize">{ed}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        {mapeo && entidadesDestino[mapeo.entidad_destino] && (
                          <div className="mt-4">
                            <Label className="mb-2 block">Mapeo de campos</Label>
                            <div className="border rounded-lg overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Campo origen</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Ejemplo</TableHead>
                                    <TableHead>→</TableHead>
                                    <TableHead>Campo destino</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {entidad.campos.map(campo => {
                                    const campoMapeo = mapeo.mapeos_campos.find(
                                      cm => cm.campo_origen === campo.nombre
                                    );
                                    return (
                                      <TableRow key={campo.nombre}>
                                        <TableCell className="font-mono text-sm">{campo.nombre}</TableCell>
                                        <TableCell>
                                          <Badge variant="outline">{campo.tipo}</Badge>
                                        </TableCell>
                                        <TableCell className="text-sm text-gray-500 max-w-32 truncate">
                                          {campo.ejemplos[0]?.toString() || '-'}
                                        </TableCell>
                                        <TableCell>
                                          <ArrowRight className="w-4 h-4 text-gray-400" />
                                        </TableCell>
                                        <TableCell>
                                          <Select
                                            value={campoMapeo?.campo_destino || ""}
                                            onValueChange={(v) => actualizarMapeoCampo(entidad.nombre, campo.nombre, v)}
                                          >
                                            <SelectTrigger className="w-40">
                                              <SelectValue placeholder="No mapear" />
                                            </SelectTrigger>
                                            <SelectContent>
                                              <SelectItem value="__none__">No mapear</SelectItem>
                                              {Object.keys(entidadesDestino[mapeo.entidad_destino].campos).map(cd => (
                                                <SelectItem key={cd} value={cd}>{cd}</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setPasoActual(2)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <Button onClick={() => setPasoActual(4)}>
              Continuar
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {pasoActual === 4 && (
        <div className="space-y-6">
          {!resultados ? (
            <>
              {/* Resumen antes de importar */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Listo para importar
                  </CardTitle>
                  <CardDescription>
                    Revisa el resumen y ejecuta la importación
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analisis.entidades.filter(e => entidadesSeleccionadas[e.nombre]).map(entidad => {
                      const mapeo = mapeos.find(m => m.entidad_origen === entidad.nombre);
                      return (
                        <div key={entidad.nombre} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-4">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                            <div>
                              <p className="font-medium">{entidad.nombre}</p>
                              <p className="text-sm text-gray-500">{entidad.registros} registros</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <ArrowRight className="w-4 h-4 text-gray-400" />
                            <Badge>{mapeo?.entidad_destino}</Badge>
                            <span className="text-sm text-gray-500">
                              ({mapeo?.mapeos_campos.length || 0} campos mapeados)
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {analisis.entidades.filter(e => !entidadesSeleccionadas[e.nombre]).length > 0 && (
                      <div className="pt-4 border-t">
                        <p className="text-sm text-gray-500 mb-2">Entidades no seleccionadas:</p>
                        <div className="flex flex-wrap gap-2">
                          {analisis.entidades.filter(e => !entidadesSeleccionadas[e.nombre]).map(e => (
                            <Badge key={e.nombre} variant="outline" className="text-gray-500">
                              {e.nombre}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {importando && (
                    <div className="mt-6">
                      <Progress value={progresoImportacion} className="h-2" />
                      <p className="text-center text-sm text-gray-500 mt-2">Importando datos...</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setPasoActual(3)}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
                <Button onClick={ejecutarImportacion} disabled={importando}>
                  {importando ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importando...</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" />Ejecutar importación</>
                  )}
                </Button>
              </div>
            </>
          ) : (
            /* Resultados */
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                  Importación completada
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {resultados.map((resultado, idx) => (
                    <div key={idx} className={`p-4 rounded-lg ${resultado.errores > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {resultado.errores === 0 ? (
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                          ) : (
                            <AlertTriangle className="w-6 h-6 text-yellow-600" />
                          )}
                          <div>
                            <p className="font-medium">{resultado.entidad}</p>
                            <p className="text-sm text-gray-600">
                              {resultado.importados} de {resultado.total} registros importados
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant={resultado.errores > 0 ? "warning" : "success"}>
                            {resultado.errores > 0 ? `${resultado.errores} errores` : 'Completo'}
                          </Badge>
                        </div>
                      </div>
                      {resultado.detalles_errores.length > 0 && (
                        <div className="mt-3 p-2 bg-white rounded border">
                          <p className="text-sm font-medium text-yellow-800">Errores:</p>
                          <ul className="text-sm text-yellow-700 list-disc list-inside">
                            {resultado.detalles_errores.map((e, i) => <li key={i}>{e}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <div className="mt-6 flex justify-center gap-4">
                  <Button variant="outline" onClick={reiniciar}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Nueva importación
                  </Button>
                  <Button onClick={() => navigate('/configuracion')}>
                    Finalizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Dialog Preview */}
      <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Vista previa: {previewEntidad}</DialogTitle>
            <DialogDescription>Primeros 5 registros de los datos</DialogDescription>
          </DialogHeader>
          {previewData && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {previewData[0] && Object.keys(previewData[0]).slice(0, 8).map(key => (
                      <TableHead key={key} className="text-xs">{key}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.map((row, idx) => (
                    <TableRow key={idx}>
                      {Object.values(row).slice(0, 8).map((val, i) => (
                        <TableCell key={i} className="text-xs max-w-32 truncate">
                          {val?.toString() || '-'}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImportacionDatos;
